import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Users, Lock, ShieldCheck, CreditCard, UserPlus } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { AccessPassDialog } from "@/components/control/AccessPassDialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Section, Loading, ErrorBox, Empty, Status, RowLink, SelectField, TextField, can, errText, fmtDay,
} from "@/components/control/kit";

const STATUS = [{ value: "all", label: "Alle Status" }, { value: "aktiv", label: "Aktiv" }, { value: "ausstehend", label: "Ausstehend" }, { value: "gesperrt", label: "Gesperrt" }];

function InviteButton() {
  return (
    <Link to="/mitglieder/einladen" data-testid="invite-member"
      className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand-magenta px-4 text-sm font-bold text-white hover:bg-brand-magenta-dark">
      <UserPlus className="h-4 w-4" aria-hidden="true" />Mitglied einladen</Link>
  );
}

export default function Members() {
  const { user } = useAuth();
  const [state, setState] = useState({ members: null, orgs: [], error: null });
  const [status, setStatus] = useState("all");
  const [org, setOrg] = useState("all");
  const [q, setQ] = useState("");
  const [passMember, setPassMember] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/members?${new URLSearchParams({ status, organization: org, q })}`);
      setState({ members: data.members, orgs: data.organizations, error: null });
    } catch (err) {
      setState((s) => ({ ...s, error: errText(err), status: err.response?.status }));
    }
  }, [status, org, q]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  return (
    <div>
      <PageHeader title="Mitglieder" subtitle="Öffentliche Profilangaben und Kontostatus. Access-Pass-Daten bleiben privat." icon={Users}
        actions={can(user, "members:manage") && <InviteButton />} />
      <div className="mb-5 grid gap-3 rounded-xl border border-border bg-white p-4 sm:grid-cols-[1fr_200px_220px]">
        <TextField id="members-q" label="Suche nach Name oder Ort" value={q} onChange={setQ} testId="members-search" />
        <SelectField id="members-status" label="Status" value={status} onChange={setStatus} options={STATUS} testId="members-status-filter" />
        <SelectField id="members-org" label="Organisation" value={org} onChange={setOrg} testId="members-org-filter"
          options={[{ value: "all", label: "Alle Organisationen" }, ...state.orgs.filter(Boolean).map((o) => ({ value: o, label: o }))]} />
      </div>
      <Section title={`Mitgliederliste${state.members ? ` (${state.members.length})` : ""}`} testId="members-section">
        {state.error ? <ErrorBox error={state.error} status={state.status} onRetry={load} /> : state.members === null ? <Loading /> : (
          <div className="overflow-x-auto">
            <Table data-testid="members-table">
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Ort</TableHead><TableHead>Organisation</TableHead><TableHead>Status</TableHead>
                <TableHead>Profilprüfung</TableHead><TableHead>Mitglied seit</TableHead><TableHead className="text-right">Access Pass</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {state.members.length === 0 && <TableRow><TableCell colSpan={7}><Empty /></TableCell></TableRow>}
                {state.members.map((m) => (
                  <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                    <TableCell><RowLink to={`/mitglieder/${m.id}`} testId={`member-link-${m.id}`}>{m.name}</RowLink></TableCell>
                    <TableCell>{m.city}</TableCell>
                    <TableCell>{m.organization || "–"}</TableCell>
                    <TableCell><Status value={m.status} /></TableCell>
                    <TableCell><Status value={m.verification} /></TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{fmtDay(m.joined_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setPassMember(m)} data-testid={`access-pass-button-${m.id}`}
                        className={`gap-1.5 ${m.access_pass_consent ? "border-green-300 text-green-700" : "border-orange-300 text-orange-700"}`}>
                        {m.access_pass_consent ? <ShieldCheck className="h-4 w-4" /> : <Lock className="h-4 w-4" />}<CreditCard className="h-4 w-4" />
                        <span className="sr-only">Access Pass von {m.name}: {m.access_pass_consent ? "Freigabe aktiv" : "keine Freigabe"}</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
      <AccessPassDialog member={passMember} open={!!passMember} onOpenChange={(o) => { if (!o) setPassMember(null); }} onChange={load} />
    </div>
  );
}
